import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useGLTF, useAnimations } from '@react-three/drei';
import { ktx2Extend } from '../utils/ktx2';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { holdBody, letGoBody } from '../services/bodyMemory';
import { resolvePose } from '../poses';
import * as THREE from 'three';

// Scratch for the per-frame pose maths — allocating a Quaternion per bone per
// frame would churn the GC in a room full of posed people.
const LIFE_E = new THREE.Euler();
const LIFE_Q = new THREE.Quaternion();

/* ⚠ THE LEGS BEND BY THE BODY, NOT BY THE BONE (2026-10-31). Khari, of two
   artists sitting at the Jahva House tables: "siting bones are backwards at the
   waist" — torso to the table, thighs swung out BEHIND the chair. poses.js
   offsets are rotations in each bone's OWN frame, tuned on the first rig
   (Meshy), whose thigh axis happens to make +X "knee forward". A body rigged
   another way has its thigh's local X pointing elsewhere, and the same +88°
   swings the leg backward. So the leg bones (thighs and knees) are bent the way
   the table MEANS: a positive X moves the bone's child FORWARD — the way the
   body faces, read off its own feet (ankle → toe) — about the body's left-right
   axis; negative moves it back (+88 thighs: knees forward; −82 knees: feet back
   under them). That is what the values already did on the first rig, so it
   changes nothing there and fixes every rig that is not it. Y/Z (a few degrees
   of splay) stay in the bone's frame. */
const LEG_FLEX = new Set(['leftupleg', 'rightupleg', 'leftleg', 'rightleg']);
const UP = new THREE.Vector3(0, 1, 0);

/** The way the body faces, in world space, from its feet at bind (null when the rig has no toes). */
function bodyForward(map) {
  const f = new THREE.Vector3();
  for (const side of ['left', 'right']) {
    const foot = map.get(`${side}foot`)?.bone; const toe = map.get(`${side}toebase`)?.bone;
    if (!foot || !toe) continue;
    f.add(toe.getWorldPosition(new THREE.Vector3()).sub(foot.getWorldPosition(new THREE.Vector3())));
  }
  f.y = 0;
  return f.lengthSq() > 1e-10 ? f.normalize() : null;
}

/**
 * A leg bone's flexion as an offset in its own frame: `rad` about the body's
 * left-right axis, signed so a positive angle carries its child bone forward.
 * World rotation R at bind, brought into the bone's frame: B⁻¹·P⁻¹·R·P·B.
 * Needs the skeleton AT BIND with world matrices current.
 */
function legFlex(bone, rad, forward) {
  const child = bone.children.find((c) => c.isBone || c.type === 'Bone');
  if (!child || !bone.parent) return null;
  const P = bone.parent.getWorldQuaternion(new THREE.Quaternion());
  const B = bone.quaternion.clone();
  const v = child.getWorldPosition(new THREE.Vector3()).sub(bone.getWorldPosition(new THREE.Vector3()));
  const L = new THREE.Vector3().crossVectors(forward, UP).normalize();
  const s = v.clone().applyAxisAngle(L, 0.2).dot(forward) >= v.dot(forward) ? 1 : -1;
  const R = new THREE.Quaternion().setFromAxisAngle(L, s * rad);
  return B.clone().invert().multiply(P.clone().invert()).multiply(R).multiply(P).multiply(B);
}
import { useWorldStore } from '../state/worldStore';
import { gaitBridge } from '../state/gaitBridge';
import { talkBridge } from '../state/talkBridge';
import { useSceneChat } from './SceneChat';
import { faceBridge } from '../state/faceBridge';
import { facePanelFor, facePanelKey } from './three/facePanel';
import { mountFaceDecal } from './three/faceMount';
import { createMouthDriver, hasFaceShapes } from './three/faceRig';
import { captureRest, retargetClip } from './three/clipRetarget';
import { loadLibraryClip, loadClipUrl, activityOverride } from '../services/clipLibrary';
import { createPoseFix, hasPoseFix } from './three/poseFix';
import { resolveShoulderDrop } from './three/shoulderBand';
import { resolveGloss, setMaterialGloss } from './three/surfaceGloss';
import { createLampUniforms, patchLampMaterial, setLamps } from '../utils/residentLamps';

// First-load wall-clock per mesh URL — see the timing note at the useGLTF
// call. Keyed by URL so twelve residents sharing a body time it once.
const BODY_LOAD_START = new Map();

// Resident lamp intensities, expressed AT A 1.8m BODY — each resident scales
// them down by (its height / 1.8)², so the authored look holds at any size.
// CHARACTER LAMPS, THIRD CALIBRATION (Khari, 2026-08-02). The history is the
// lesson: 6/2.5 washed skin to near-white → 4/1.4 still bloomed faces up
// close (the key sat in FRONT of the face at chest height, so an approaching
// camera stood next to the light source and caught the near-mirror specular)
// → fully OFF proved too dark at night. So: a QUARTER of the old energy,
// and the key moved UP above the head — it models like a streetlight now,
// throwing its specular DOWN at the pavement instead of into the camera —
// with a tighter falloff so the pool under the feet stops reading as a
// placement marker. ?lit=0 kills them for A/B; ?litkey/?litfill tune live.
const qp = (k, d) => {
  const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get(k));
  return Number.isFinite(v) ? v : d;
};
// 3 / 2 — Khari's eye, tuned live (~5:30pm world time, 2026-08-02). Note that
// 3 works where the old 4 bloomed: the GEOMETRY changed, not just the number —
// overhead placement throws the hot angle at the ground, so the same energy
// reads as presence instead of washout.
const LIT = qp('lit', 1);
const KEY = qp('litkey', 3);
const FILL = qp('litfill', 2);

// ── INDOORS IS A DIFFERENT ROOM, LITERALLY ──────────────────────────────────
//
// Khari, 2026-08-04: "the characters in the room are inheriting their shading
// from the time of day outside.. so if it is night, they are dark within the
// rooms. we should use a light from the room, or something brighter like
// morning or golden hour if the room's lighting doesn't work."
//
// The rig above was tuned on the STREET at ~5:30pm, where sun and sky do most
// of the work and these two lamps only add presence. Indoors there is no sun —
// and in a SPLAT room there is no room lighting either, because a gaussian splat
// is unlit by construction: it carries its own baked exposure and scene lights
// pass straight through it. So a body standing in a photoreal shop is lit by
// whatever the outdoor sky happens to be doing, which at night is nothing.
//
// Two compounding reasons it goes so dark: intensity scales by (height/1.8)²,
// and an interior body is authored at ~0.85 — so the street numbers arrive
// indoors at about a fifth of their strength.
//
// These lamps are POINT lights with a bounded distance on purpose. An ambient or
// hemisphere light would fix the body and wash the room with it — fine in a
// splat (nothing else is lit) and ruinous in a mesh venue whose practicals were
// tuned by eye. Bounded lights travel with the body and leave the room alone.
//
// GOLDEN HOUR rather than daylight: warm key, cool fill, and a rim to lift the
// silhouette off a dark wall. Tune live — ?inkey / ?infill / ?inrim — the same
// way the street pair was tuned.
const IN_KEY = qp('inkey', 8);
const IN_FILL = qp('infill', 4);
const IN_RIM = qp('inrim', 2.5);
// Material corrections applied to every rigged avatar on load — see the clone
// memo for why. ?emissive=1 puts the self-lit look back; ?metal= tunes the
// other half (0 = pure dielectric, which is what skin and cloth are).
const KEEP_EMISSIVE = qp('emissive', 0) > 0;
// Frustum culling for bodies (see the clone memo). 1.8x the posed radius is
// wider than any clip in the slate reaches.
const CULL_BODIES = qp('cullbodies', 1) > 0;
const BODY_CULL_PAD = 1.8;
const METALNESS = qp('metal', 0);
// `?metal=` set at all = the old A/B override (every body that metalness, maps as
// shipped); absent = each body's own surface, matte unless its owner slid it up.
const METAL_OVERRIDE = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('metal');

// AnimatedResident — a rigged avatar (image→3D via Rodin, auto-rigged + animated
// via fal Meshy). ONE skinned mesh in memory (from meshUrl); the walk/idle clips
// are extracted from the other GLBs and registered on a single mixer — Meshy's
// consistent bone names let a clip authored in another file drive this skeleton.
// (Roster-scale follow-up: merge to a single GLB offline so it's one download,
// not N. Crowd extras stay sprites — this is for HERO residents only.)
//
//   meshUrl   the rigged mesh (bind pose)
//   clipUrls  GLBs to pull AnimationClips from (walk, idle, …)
//   play      substring of the clip to play (e.g. 'walk'|'idle'); default first
//   speed     forwarded to the parent for velocity-synced timeScale (see World)
/**
 * A clip whose hips are moved across the ground so its last frame ('end') or its
 * average ('mean') sits over the body's origin (see `rootAt`). Height and the
 * pose are untouched. Cached per clip and mode; clips without a hips position
 * track come back as they were.
 */
const rerooted = new WeakMap();
function rerootClip(clip, mode) {
  let byMode = rerooted.get(clip);
  if (!byMode) { byMode = new Map(); rerooted.set(clip, byMode); }
  if (byMode.has(mode)) return byMode.get(mode);
  let out = clip;
  const ti = clip.tracks.findIndex((t) => /hips\.position$/i.test(t.name));
  if (ti >= 0) {
    const tr = clip.tracks[ti]; const v = tr.values; const n = tr.times.length;
    let rx = 0; let rz = 0;
    if (mode === 'end') { rx = v[(n - 1) * 3]; rz = v[(n - 1) * 3 + 2]; } else { for (let i = 0; i < n; i++) { rx += v[i * 3]; rz += v[i * 3 + 2]; } rx /= n; rz /= n; }
    const values = Float32Array.from(v);
    for (let i = 0; i < n; i++) { values[i * 3] -= rx; values[i * 3 + 2] -= rz; }
    const tracks = clip.tracks.slice();
    tracks[ti] = new THREE.VectorKeyframeTrack(tr.name, tr.times, values);
    out = new THREE.AnimationClip(clip.name, clip.duration, tracks, clip.blendMode);
  }
  byMode.set(mode, out);
  return out;
}

export function AnimatedResident({
  meshUrl, clipUrls = [], play, height = 1.8, position = [0, 0, 0], rotationY = 0,
  timeScale = 1, beatSync = false, stepsPerLoop = 2, gaitOctave = 1, footPhase = 0, gaitBpm = null,
  talkAware = false, onSelect = null, onHover = null,
  // Indoors the body brings its own light (see the rig notes up top). `lightScale`
  // is the ROOM's own declared level (ROOMS.<id>.light) so a dim venue keeps its
  // people dim and a bright one keeps them bright — the room decides, and the
  // fallback when it has no opinion is golden hour rather than whatever the sky
  // is doing outside.
  indoor = false, lightScale = 1,
  avatarId = null, attentionRadius = null, midRadius = null, onTier = null, onReady = null,
  phaseOffset = 0, rate = 1, // desync idle across NPCs so they don't move in lockstep
  // A POSE key ('sitting' | 'reclined' | 'barber' | 'browsing' | 'leaning', see
  // poses.js) makes this body a fixture rather than a walker: no clip, no
  // mixer, bones bent from the bind pose and kept alive procedurally. Unknown
  // or absent = a normal resident, because a typo in a placement's `data.pose`
  // must degrade to a standing NPC, never an empty room.
  pose = null,
  /* The body's OWN stored rig data from `/api/world/avatar-models` —
     `{ shoulderDrop, armOut, elbowIn, headTilt, armLiftMax, face: {…} }`.
     ⚠ NOT `pose` above, which is the fixture key. See the block below. */
  rigPose = null,
  /* The body's gesture slate, `{listening_gesture: url, big_wave_hello: url, …}`.
     Needed because `rigPose.restClip` is a NAME and the clip it names lives
     here — pose without map is a decision that can be read and not acted on. */
  clipMap = null,
  /* THEIR STAGE WALK (2026-11-04). Khari: *"be sure to use their stage walk when
     they are moving to new locations on the stage … or tell them to walk.. their
     choice of which to use."* `gaitUrl` is the body's own `stage_walk` file,
     fetched only while it is set (a performer on stage), and `gait` says whether
     to walk with it NOW in place of the walk clip (city/stagePlan, the plan's
     choice per move). A body without one walks with its walk. */
  gaitUrl = null,
  gait = false,
  /* SPEAKING ON THEIR OWN (the Living City, 2026-10-23): a line of a public
     conversation with another artist is playing in this body's voice. The
     voice-chat path below only ever animates the ONE artist a visitor is talking
     to (a single global switch), so two artists talking to each other on a
     corner needed their own. Same head and face motion, its own rhythm. */
  speaking = false,
  /* AN ACTIVITY FROM THE SHARED CLIP LIBRARY (services/clipLibrary.js) — a
     dance, a drink, a rap — played on THIS body through the retarget, so any
     body can do anything in the library without being re-rigged. Null keeps the
     usual walk/idle. An admin `?act=` in the URL overrides it (see clipLibrary). */
  activity = null,
  /* THE ACTIVITY IS THEIR TALKING (2026-11-04, city/bodyLanguage: hands while they
     speak). A live voice call still stops a dance or a habit ("a person stops
     what they are doing to talk to you"), but not the gestures of the talking
     itself: Khari, of Avva in the Blackroom, "she was unable to emote". */
  activityTalks = false,
  /* SEATED (2026-11-04: a café chair, a helicopter seat). With `seatHips` set, the
     activity is a recorded seated move and this is where its hips belong, in world
     units above this body's origin (a cushion). The retarget keeps a clip's
     vertical travel in the source rig's units, so how low a seated clip sits
     differs per body: it is measured on the fitted clip (its average hip height)
     and the body is lowered to put the hips there. Hidden until the move is
     fitted, so a seated body never stands up through the seat (or T-poses). */
  seatHips = null,
  /* ONE CLIP, ONCE, FROM PART-WAY, ON A MARK (2026-11-03, the heli pilot sitting
     down: Khari, "the helicopter pilot was made with a sitting pose as well as a
     walk to sit animation … so she can walk to the helicopter and sit while
     flying it"). For a body directed through a few clips by its parent:
       playOnce  the `play` clip runs once and HOLDS its last frame (a sit-down)
       playFrom  …starting this many seconds in (a clip's useful end)
       rootAt    { clipNameSubstring: 'end' | 'mean' } — move that clip's hips
                 across the ground so its LAST frame ('end') or its average
                 ('mean') sits over this body's origin. A sit-down ending at
                 `end` and a seated idle at `mean` then put the hips on the same
                 seat, and one hands to the other without a jump. */
  playOnce = false, playFrom = null, rootAt = null,
}) {
  // Engaged = THIS NPC is the active voice-chat target (matched by avatarId) —
  // or, for the legacy single-resident mount, simply talkAware. Only the engaged
  // NPC animates talk / follows face capture, so a crowd doesn't all move at once.
  const chatAvatar = useWorldStore((s) => s.chatAvatar);
  const residentTalking = useWorldStore((s) => s.residentTalking);
  const engaged = avatarId != null ? chatAvatar?.avatarId === avatarId : talkAware;
  const voiceTalking = engaged && residentTalking;
  /* …OR READING YOU A TYPED REPLY (2026-11-01, Khari: "when sitting at a table
     talking with an artist, their talking face should be activated if
     available. (same for the scene chats)"). A typed reply has no audio, so the
     chat says how long it takes to say (SceneChat useChatSpeaking) and the face
     reads it out — the same speech-like rhythm as a conversation line. */
  const chatSaying = useSceneChat((st) => st.speaking != null && st.speaking === Number(avatarId)) && engaged;
  const saying = speaking || chatSaying;
  const talking = voiceTalking || saying;
  /* ── THE RESTING POSE, AS CHOSEN IN THE EDITOR ───────────────────────────
     Khari, 2026-08-31: *"Their default poses are not showing. I have set them in
     the avatar editor, but they seem to be defaulting to something else. Our
     default listening pose should be the listening gesture if we are not able to
     define it from the editor."*

     `pose.restClip` names the clip a body holds when it is simply standing
     there — `listening_gesture` on every built body but two. The World was
     standing them in whatever landed in the `idle` SLOT at build time (`Idle_6`,
     `Idle_9`), which is a rig-order accident rather than a decision. Worse, it
     never DOWNLOADED the named clip: `clipUrls` is walk + idle and nothing else,
     so the choice could be read and never acted on.

     THE RULE, in order: the clip the editor named · else `listening_gesture` if
     this body has one · else the idle it has always used. A stored name outlives
     a rebuild, so a name this body no longer carries must mean "rest as before",
     never a T-pose. */
  const rest = useMemo(() => {
    const asked = rigPose?.restClip || null;
    if (!clipMap) return { url: null, label: null, asked };
    if (asked && clipMap[asked]) return { url: clipMap[asked], label: asked, asked };
    if (clipMap.listening_gesture) return { url: clipMap.listening_gesture, label: 'listening_gesture', asked };
    return { url: null, label: null, asked };
  }, [clipMap, rigPose?.restClip]);
  const urls = useMemo(() => {
    const list = [meshUrl, ...clipUrls];
    /* ⚠ APPENDED, NOT SUBSTITUTED. The idle stays loaded as the fallback: if
       the resting clip's GLB fails, `SafeMount` retries the WHOLE body, and a
       resident that can still stand beats one that cannot. ~130KB per body
       that has a chosen pose. */
    if (rest.url && !list.includes(rest.url)) list.push(rest.url);
    return list;
  }, [meshUrl, clipUrls, rest.url]);
  // LOAD TIMING for the lazy-load plan (Khari, 2026-08-24: swap distances
  // depend on "how long it takes to fully load a model into the system at
  // runtime"). Module-level map, not component state: this body runs,
  // SUSPENDS on useGLTF, and runs again when the fetch lands — state would
  // reset on every replay, the map survives and times the real wait.
  if (!BODY_LOAD_START.has(meshUrl)) BODY_LOAD_START.set(meshUrl, performance.now());
  // KTX2/Basis transcoding, same as the city model. Avatar meshes carry the
  // heaviest textures in the world (one 14MB PNG on a body you walk right up
  // to), and a PNG decodes to full RGBA in VRAM no matter how well it zipped.
  // The extension is INERT on GLBs with no KTX2 textures, so attaching it here
  // costs the existing avatars nothing.
  const gl = useThree((st) => st.gl);
  const ktx2 = useMemo(() => ktx2Extend(gl), [gl]);
  const gltfs = useGLTF(urls, true, true, ktx2);
  const base = Array.isArray(gltfs) ? gltfs[0] : gltfs;
  // Worn while mounted; on a phone the last one to let go frees the GPU memory
  // (services/bodyMemory: the ICT bodies crashed Safari on entry, 2026-10-29).
  const urlKey = (Array.isArray(urls) ? urls : [urls]).join('|');
  useEffect(() => {
    const list = Array.isArray(urls) ? urls : [urls];
    const files = Array.isArray(gltfs) ? gltfs : [gltfs];
    holdBody(list);
    return () => letGoBody(list, files);
  }, [urlKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t0 = BODY_LOAD_START.get(meshUrl);
    if (t0 == null) return;
    BODY_LOAD_START.delete(meshUrl);
    const ms = performance.now() - t0;
    // Sub-50ms is the cache answering, not the network — no signal in it.
    if (ms > 50) {
      let label;
      try {
        const u = new URL(meshUrl, window.location.origin);
        label = (u.searchParams.get('name') || u.pathname).split('/').pop();
      } catch { label = String(meshUrl).slice(-40); }
      console.log(`[body] ${label} ready in ${(ms / 1000).toFixed(2)}s`);
    }
  }, [meshUrl]);

  // One mesh, skeleton-safe clone so several residents can share the load.
  // Its size is measured HERE, while the clone is still unparented — see the
  // scale memo below for why that timing is load-bearing.
  const { model, intrinsicY, floorY, bindRest, hipRest } = useMemo(() => {
    const c = skeletonClone(base.scene);
    c.traverse((o) => {
      if (!o.isSkinnedMesh) return;
      // CULLED, WITH HEADROOM. This used to be `frustumCulled = false` ("don't
      // cull mid-stride") — so every loaded resident was skinned and drawn every
      // frame, including the ones behind you. The worry was real: a skinned
      // mesh's bounds are measured from ONE pose, and an arm swung outside them
      // would pop. So the sphere is measured once, from the first posed frame,
      // and then padded generously — a gesture or a stride cannot leave it, but
      // a body that is genuinely off-screen is skipped. ?cullbodies=0 restores
      // the old always-draw behaviour.
      if (CULL_BODIES) {
        o.frustumCulled = true;
        const compute = o.computeBoundingSphere.bind(o);
        o.computeBoundingSphere = function () { compute(); if (this.boundingSphere) this.boundingSphere.radius *= BODY_CULL_PAD; };
      } else {
        o.frustumCulled = false;
      }
      // Every avatar arrives with its BASE COLOUR also wired up as a
      // full-strength emissive map (emissiveFactor [1,1,1]). A self-illuminated
      // body ignores the scene entirely: it emits its own albedo on top of
      // whatever light reaches it, which blows skin toward white and — because
      // emissive has no direction — erases the shading gradients that read as
      // brow, nose and cheekbone. That is the "pale, flattened face" across the
      // whole cast, and it is why changing the lamps made no difference.
      //
      // They also come in at metalness 1.0, glTF's default when no
      // metallic-roughness texture is exported. Skin and cloth are not metal.
      //
      // Fixed on load rather than in the pipeline so it applies to all twelve
      // without reprocessing. ?emissive=1 restores the shipped look for A/B.
      for (const m of (Array.isArray(o.material) ? o.material : [o.material])) {
        if (!m) continue;
        if (!KEEP_EMISSIVE) m.emissiveIntensity = 0;
      }
      // OWN MATERIALS PER BODY. SkeletonUtils.clone shares them between every
      // resident built from the same GLB, and each body's lamps now live in its
      // material's uniforms (utils/residentLamps) — shared materials would light
      // every copy from wherever the first one stands. Textures stay shared.
      if (LIT > 0) o.material = Array.isArray(o.material) ? o.material.map((m) => m?.clone()) : o.material?.clone();
      /* ⚠ THE SURFACE, AFTER THE CLONE (three/surfaceGloss). This used to force
         metalness 0 on every body and keep its shine map, so the bodies built
         since ~2026-10-29 (the rigger now passes the mesh's own map through) came
         out as wet plastic: Flood of Fire, Avalo. Now matte by default, the
         body's own shine and metal as its owner slides `pose.gloss` up (Lyra's
         chrome at 1). After the clone, because the clone is what is drawn and
         it remembers its own shipped maps. */
      for (const m of (Array.isArray(o.material) ? o.material : [o.material])) {
        if (!m) continue;
        if (METAL_OVERRIDE) m.metalness = METALNESS;
        else setMaterialGloss(m, resolveGloss(rigPose));
      }
    });
    c.updateMatrixWorld(true);                // apply internal node scales BEFORE measuring
    const box = new THREE.Box3().setFromObject(c);
    const size = new THREE.Vector3(); box.getSize(size);
    /* The rest pose, captured HERE — before the mixer has touched a bone — is
       what every library clip is replayed against. Read later, it would be
       whatever clip happened to be playing. */
    // The hips at rest, in the model's own space (the clone is unparented): what a seated move's height is measured from.
    let hipsNode = null; c.traverse((o) => { if (!hipsNode && /^hips$/i.test(o.name)) hipsNode = o; });
    const hipRest = hipsNode?.parent ? { name: hipsNode.name, y: hipsNode.getWorldPosition(new THREE.Vector3()).y, p: hipsNode.position.clone(), m: hipsNode.parent.matrixWorld.clone() } : null;
    return { model: c, intrinsicY: size.y || 1, floorY: box.min.y, bindRest: captureRest(c), hipRest };
  }, [base.scene]);

  // The per-body material clones (see the lamps note in the memo) die with
  // the body. Textures are shared with the GLB cache and are left alone.
  useEffect(() => () => {
    if (!(LIT > 0)) return;
    model.traverse((o) => {
      if (!o.isSkinnedMesh) return;
      for (const m of (Array.isArray(o.material) ? o.material : [o.material])) m?.dispose();
    });
  }, [model]);

  // Merge every clip from every loaded GLB (mesh's own + the clip files).
  const rootKey = rootAt ? JSON.stringify(rootAt) : '';
  const clips = useMemo(() => {
    const all = (Array.isArray(gltfs) ? gltfs : [gltfs]).flatMap((g) => g.animations || []);
    if (!rootAt) return all;
    return all.map((c) => {
      const k = Object.keys(rootAt).find((key) => c.name.toLowerCase().includes(key.toLowerCase()));
      return k ? rerootClip(c, rootAt[k]) : c;
    });
  }, [gltfs, rootKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const { actions, names, mixer } = useAnimations(clips, model);


  // Target height ÷ the model's OWN height. Strictly this NPC's own target — no
  // global ?rheight override, which would force every resident to one shared
  // size (looks like they compare).
  //
  // The measurement lives in the clone memo above, and must: Box3.setFromObject
  // reads WORLD matrices, so measuring from here — where the model is already
  // mounted under <group scale={scale}> — fed the previous scale back into the
  // next one. Re-sizing a resident then computed height / (already-scaled
  // height) instead of height / (intrinsic height): the body snapped to roughly
  // its raw ~1.8m and barely moved again, so it read as stuck "too large" no
  // matter how far you wound the size down. Only the first render was ever
  // right, because that is the one where the clone had no parent yet.
  //
  // Intrinsic size is a property of the mesh, not of how it is currently drawn.
  // Measure it once, off the unparented clone, and scale is honest every time.
  const { scale, yOff } = useMemo(
    () => ({ scale: height / intrinsicY, yOff: -floorY * (height / intrinsicY) }),
    [intrinsicY, floorY, height],
  );

  // Per-resident key + fill, so a body stays readable when the district is dark.
  //
  // Their POSITIONS are expressed in units of `height` — correct, a lamp should
  // sit just above the head — but their INTENSITY was a constant authored when
  // residents rendered at ~1.8m. Once heights were tuned down to ~0.8 the lamps
  // moved to roughly 0.7m off the face while still burning at the brightness
  // chosen for 1.6m away. Illuminance goes as 1/d², so they landed about 5×
  // hotter than intended, pointed straight at the face, and blew out precisely
  // the shading gradients that read as facial structure. Warm cream on one side
  // and pale blue on the other then desaturated skin toward white.
  //
  // Scaling intensity by (height/1.8)² restores the authored look at ANY tuned
  // height: geometry and brightness now shrink together instead of fighting.
  const hScale = height / 1.8;
  const lampFalloff = hScale * hScale;

  // Fires once the mesh is genuinely in the scene — useGLTF suspends, so this
  // effect cannot run until the GLB has been fetched, parsed and cloned. That
  // makes it the honest "I am standing" signal for the streaming queue, rather
  // than a timer guessing at how long a body takes to arrive.
  useEffect(() => { onReady?.(); }, [model, onReady]);

  /* ⚠ MATCHED BY SOURCE, NEVER BY NAME. The GLB calls it
     `Armature|Listening_Gesture|baselayer` while the map calls it
     `listening_gesture`, so a name match works only as long as every rigger
     spells it the same way — and when it stops working the body just rests in
     the wrong pose, silently. `useGLTF` returns results in URL order, so the
     index of the clip's own file is the answer and cannot drift. */
  const restName = useMemo(() => {
    if (!rest.url) return null;
    const i = urls.indexOf(rest.url);
    const g = i >= 0 && Array.isArray(gltfs) ? gltfs[i] : null;
    const n = g?.animations?.[0]?.name || null;
    return n && names.includes(n) ? n : null;
  }, [rest.url, urls, gltfs, names]);

  // Locomotion clip vs the resting pose to stand in while talking.
  const walkName = useMemo(() => {
    /* ⚠ A RESIDENT ASKED TO "idle" RESTS IN ITS CHOSEN POSE. Every standing
       mount passes play="idle", which substring-matched the idle clip's own
       name — and the chosen clip is not called "idle", so it could never win
       however correctly it was stored. This is the line the setting died on. */
    if (restName && (!play || /idle/i.test(play))) return restName;
    return (play && names.find((n) => n.toLowerCase().includes(play.toLowerCase()))) || names[0];
  }, [names, play, restName]);
  const idleName = useMemo(() => restName || names.find((n) => /idle/i.test(n)), [names, restName]);

  // The head bone (Meshy names it "Head") — the target for the talking gesture.
  // ── POSED BODIES ──
  // A posed body must NOT run the idle clip: the mixer writes these same bone
  // channels every frame and would win the fight, leaving a barber who twitches
  // back to attention 60 times a second. Posed = fully procedural, which is
  // also why it is cheap — no clip download, no mixer, no extra GLB.
  const posed = useMemo(() => resolvePose(pose), [pose]);
  // Who this body IS, for logs. `avatarId` is not threaded in from
  // StagedResident and wiring it would change `engaged` (the chat-target test),
  // which is not worth a behaviour change for a log line — the mesh filename
  // names the character just as well.
  /* ⚠ A REMOTE BODY'S URL IS A PROXY QUERY, NOT A PATH. `.split('/').pop()` on
     `/api/assets/file?bucket=…&name=bodies%2F24.glb` yields the whole query
     string, so every log line about a GCS-served body was unreadable — which is
     precisely the body you need named when a face or a correction misbehaves.
     Same extraction the load-timing effect above already uses. */
  const who = useMemo(() => {
    let label;
    try {
      const u = new URL(meshUrl, window.location.origin);
      label = (u.searchParams.get('name') || u.pathname).split('/').pop();
    } catch { label = String(meshUrl || '').split('/').pop(); }
    return (label || 'resident').replace(/_rigged\.glb$/, '').replace(/\.glb$/, '');
  }, [meshUrl]);

  /* ⚠ SAY WHICH POSE WON. Three outcomes look identical on screen — the editor's
     choice, the listening fallback, and the old idle slot — so a body resting
     "wrong" is otherwise unattributable. Warns only when a stored name is not on
     the body, which is the one case that means something is actually stale. */
  useEffect(() => {
    if (!clipMap) return;
    if (restName) {
      const via = rest.label === rest.asked ? 'set in the editor' : 'default (no editor choice)';
      console.log(`[pose] ${who} rests in ${rest.label} — ${via}`);
    } else if (rest.asked) {
      console.warn(`[pose] ${who}: restClip "${rest.asked}" is not on this body and it has no`
        + ` listening_gesture — resting in the idle slot. Known clips: ${Object.keys(clipMap).join(', ') || 'none'}`);
    }
  }, [restName, rest.label, rest.asked, who, clipMap]);

  // THE BIND POSE, CAPTURED BEFORE ANYTHING ANIMATES. Offsets are measured FROM
  // the rest pose, so the rest pose has to be read while it is still the rest
  // pose. Effects run before the first useFrame, so the mixer has not written a
  // channel yet here — read it one frame later and a mid-stride rotation
  // becomes "rest", which is one way to end up with a deformed body.
  const bindRef = useRef(new Map());   // lowercased bone name → { bone, base }
  useEffect(() => {
    const m = new Map();
    model.traverse((o) => {
      if (o.isBone || o.type === 'Bone') m.set((o.name || '').toLowerCase(), { bone: o, base: o.quaternion.clone() });
    });
    bindRef.current = m;
  }, [model]);

  // A pose is only LIVE once its bones are actually found on this rig. State,
  // not a ref, because the clip effect below has to react to it: standing the
  // mixer down for a pose that then applies nothing leaves the body in its bind
  // pose — a T-POSE (Khari, 2026-08-06: "when I refreshed they all came back in
  // T-poses"). Better a plain idling resident than a mannequin with its arms out.
  const poseBones = useRef([]);   // [{ bone, base:Euler, off:[x,y,z] }]
  const [poseLive, setPoseLive] = useState(false);
  useEffect(() => {
    // PUT EVERY BONE BACK FIRST (Khari, 2026-08-06: "the poses seem to change a
    // bit, depending on the order they were applied", and akra deformed further
    // with each change).
    //
    // A pose only writes the bones it NAMES. Switching from one that bends the
    // legs to one that says nothing about legs left those legs bent — forever,
    // because nothing owned putting them back. So every pose was really
    // "previous pose, plus this one", and the body drifted further from human
    // with each change. Resetting to bind here makes a pose ABSOLUTE rather
    // than a diff against whatever happened to be on the body already.
    //
    // Unconditional, including when the pose is being removed: the idle clip
    // only animates the channels it owns, so any bone it does not touch would
    // otherwise keep its posed rotation and follow the body around standing up.
    for (const { bone, base: b } of bindRef.current.values()) bone.quaternion.copy(b);

    if (!posed) {
      if (pose) console.warn(`[pose] ${who} has data.pose="${pose}" but no such pose exists — standing.`);
      poseBones.current = []; setPoseLive(false); return;
    }
    // Case-insensitive because the rig is inconsistent about it — `neck` is
    // lowercase while `Head` is not, and that is the rig's business, not the
    // pose table's.
    const map = bindRef.current;
    // The skeleton is at bind now (reset above): bring its world matrices up to
    // date so the legs can be bent by the body (LEG_FLEX), whichever way this rig's axes run.
    model.updateWorldMatrix(true, true);
    const forward = bodyForward(map);
    const list = Object.entries(posed.bones)
      .map(([name, off]) => {
        const e = map.get(name.toLowerCase());
        if (!e) return null;
        if (forward && LEG_FLEX.has(name.toLowerCase())) {
          const flex = legFlex(e.bone, off[0], forward);
          if (flex) return { bone: e.bone, base: e.base, offQ: flex.multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(0, off[1], off[2], 'XYZ'))) };
        }
        // COMPOSE, never add. `bone.rotation.set(base.x + off.x, …)` adds Euler
        // COMPONENTS, and a bind rotation here is an arbitrary quaternion — so
        // adding to its Euler decomposition does not rotate the bone BY the
        // offset, it lands somewhere unrelated. That is why measured-correct
        // axes still produced bent, sheared bodies (Khari, 2026-08-06: "my
        // models are becoming more deformed as we go"). An offset is a rotation
        // applied IN THE BONE'S OWN FRAME: bind × offset.
        const offQ = new THREE.Quaternion().setFromEuler(new THREE.Euler(off[0], off[1], off[2], 'XYZ'));
        return { bone: e.bone, base: e.base, offQ };
      })
      .filter(Boolean);
    poseBones.current = list;
    setPoseLive(list.length > 0);
    // Say which branch this body took, by name. Akra (66) posed like everyone
    // else and then came back a T-pose, with assets PROVEN identical to the
    // other fifteen — so the difference is in state, not in the rig, and it has
    // to be visible rather than deduced.
    console.info(`[pose] ${who} "${posed.key}" → ${list.length}/${Object.keys(posed.bones).length} bones${list.length ? '' : ' (NOT live — idle clip keeps running)'}`);
    if (!list.length) {
      console.warn(`[pose] "${posed.key}" matched NO bones on this rig — playing the idle clip instead so the body is not left in a T-pose. `
        + `This rig's bones: ${[...map.keys()].join(', ') || '(none found)'}`);
    } else if (list.length < Object.keys(posed.bones).length) {
      const missing = Object.keys(posed.bones).filter((n) => !map.has(n.toLowerCase()));
      console.warn(`[pose] "${posed.key}" is missing ${missing.length} bone(s) on this rig: ${missing.join(', ')}`);
    }
  }, [model, posed]);

  const headBone = useRef(null);
  const headBase = useRef(new THREE.Euler());
  useEffect(() => {
    let found = null;
    model.traverse((o) => {
      if (found || !(o.isBone || o.type === 'Bone')) return;
      const n = (o.name || '').toLowerCase();
      if (n === 'head' || (n.includes('head') && !n.includes('end') && !n.includes('front'))) found = o;
    });
    headBone.current = found;
    if (found) headBase.current.copy(found.rotation);
  }, [model]);

  /* ── the activity (shared clip library) ──
     ⚠ DECLARED DOWN HERE, AFTER `who`: it logs with it, and a `const` read above
     its declaration is a TDZ crash for every resident (caught in the harness).
     Fetched once per clip name, retargeted onto THIS body's rest, and handed to
     the mixer as one more action. The clip-choosing effect below plays it in
     place of walk/idle while it is set. */
  const act = activity || activityOverride(avatarId);
  const [actClip, setActClip] = useState(null);
  /* ⚠ A SET CHANGES MOVE DOZENS OF TIMES (city/stagePlan, 2026-11-04), so the
     moves this body has been fitted with are kept, six at most, and one that
     drops out is released from the mixer. Before, every change fitted the move
     again and kept every old one as a live action, for as long as the body lived. */
  const fitted = useMemo(() => new Map(), [bindRest]);   // move name → this body's clip
  useEffect(() => {
    if (!act) { setActClip(null); return undefined; }
    const had = fitted.get(act);
    if (had) { fitted.delete(act); fitted.set(act, had); setActClip(had); return undefined; }
    let alive = true;
    loadLibraryClip(act).then(({ clip, root }) => {
      if (!alive) return;
      const r = retargetClip(clip, root, bindRest);
      console.log(`[clips] ${who}: ${act} (${r.clip.duration.toFixed(1)}s, rest gap ${r.maxRestGapDeg.toFixed(0)}° on ${r.worstBone || '—'})`);
      fitted.set(act, r.clip);
      while (fitted.size > 6) {
        const [oldName, old] = fitted.entries().next().value;
        fitted.delete(oldName);
        if (mixer) { mixer.uncacheAction(old, model); mixer.uncacheClip(old); }
      }
      setActClip(r.clip);
    }).catch((e) => console.warn(`[clips] ${who}: could not load "${act}" — ${e.message}`));
    return () => { alive = false; };
  }, [act, bindRest, who, fitted, mixer, model]);
  const actAction = useMemo(() => (actClip && mixer ? mixer.clipAction(actClip, model) : null), [actClip, mixer, model]);
  // SEATED (`seatHips`): how far to lower this body so the fitted move's average hips land there.
  const seatOff = useMemo(() => {
    if (seatHips == null || !actClip || !hipRest) return 0;
    const tr = actClip.tracks.find((x) => x.name.toLowerCase() === `${hipRest.name.toLowerCase()}.position`);
    if (!tr?.values?.length) return 0;
    let sum = 0;
    for (let i = 1; i < tr.values.length; i += 3) sum += tr.values[i];
    const sat = hipRest.p.clone(); sat.y = sum / (tr.values.length / 3);
    const dy = sat.applyMatrix4(hipRest.m).y - hipRest.p.clone().applyMatrix4(hipRest.m).y;
    return seatHips - (hipRest.y + dy - floorY) * scale;
  }, [seatHips, actClip, hipRest, floorY, scale]);
  const seatWaiting = seatHips != null && !actClip;

  // Their stage walk, fitted like any clip (exact on its own rig, played in place).
  const [gaitClip, setGaitClip] = useState(null);
  useEffect(() => {
    if (!gaitUrl) { setGaitClip(null); return undefined; }
    let alive = true;
    loadClipUrl(gaitUrl).then(({ clip, root }) => {
      if (alive) setGaitClip(retargetClip(clip, root, bindRest).clip);
    }).catch((e) => console.warn(`[clips] ${who}: could not load their stage walk — ${e.message}`));
    return () => { alive = false; };
  }, [gaitUrl, bindRest, who]);
  const gaitAction = useMemo(() => (gaitClip && mixer ? mixer.clipAction(gaitClip, model) : null), [gaitClip, mixer, model]);

  // Play walk normally; crossfade to idle while speaking (a talking person
  // stands). The beat-locked gait only scrubs the WALK clip; idle plays freely.
  const activeAction = useRef(null);
  useEffect(() => {
    // A library move needs no clip of the body's own (a seated body, 2026-11-04): only
    // with neither, and no live pose, is the body left in its bind pose.
    if (!names?.length && !(actAction && !poseLive)) {
      if (!poseLive && !seatWaiting) console.warn(`[pose] ${who} has NO animation clips loaded — it will stand in a T-pose.`);
      return undefined;
    }
    // Posed bodies stand the mixer down entirely — see the pose block above.
    // `poseLive`, not `posed`: a pose whose bones were not found must keep its
    // clip, or the body drops to its bind pose.
    if (poseLive) {
      activeAction.current?.stop();
      activeAction.current = null;
      return undefined;
    }
    const target = (talking && idleName) ? idleName : walkName;
    // Walking, and their set calls for the stage walk: that in place of the walk clip.
    const walkingNow = target === walkName && /walk/i.test(play || '');
    const base = walkingNow && gait && gaitAction ? gaitAction : actions[target];
    /* An activity wins over walk/idle — except live voice chat, where a person
       stops what they are doing to talk to you. */
    const action = (actAction && (!voiceTalking || activityTalks)) ? actAction : base;
    if (!action || activeAction.current === action) return undefined;
    activeAction.current?.fadeOut(0.25);
    action.reset().fadeIn(0.25).play();
    action.timeScale = timeScale * rate;                     // per-NPC rate → drift apart
    if (!beatSync) action.time = phaseOffset * (action.getClip()?.duration || 0); // desync start
    // Directed (playOnce / playFrom, above): this clip once, from its useful part, then held.
    const directed = action === actions[walkName] && !actAction;
    if (directed && playOnce) { action.setLoop(THREE.LoopOnce, 1); action.clampWhenFinished = true; }
    else { action.setLoop(THREE.LoopRepeat, Infinity); action.clampWhenFinished = false; }
    if (directed && playFrom != null) action.time = Math.max(0, Math.min(playFrom, (action.getClip()?.duration || 0) - 0.05));
    action.paused = beatSync && action === actions[walkName]; // scrub only the gait clip — never an activity
    activeAction.current = action;
    return undefined;
  }, [actions, names, walkName, idleName, talking, timeScale, beatSync, phaseOffset, rate, poseLive, actAction, voiceTalking, activityTalks, playOnce, playFrom, gait, gaitAction, play]);

  // Beat-LOCKED gait — phase-lock the clip's time to the world beat clock (same
  // free-running grid the lights pulse on: beat count = t·bpm/60) so the foot
  // PLANTS on the beat, not merely at the right average speed. The step rate is
  // a power-of-two subdivision of the beat (sliding scale: fast song → half- then
  // quarter-time strut, never a run; slow song → double-time). gaitOctave shifts
  // it down (2 = half as often). footPhase aligns the clip's foot-contact frame
  // to the downbeat — tune with ?footphase=0..1 until the plant lands on beat.
  const q = (k) => { const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get(k)); return Number.isFinite(v) ? v : null; };
  useFrame((state) => {
    const action = activeAction.current;
    if (!beatSync || !action || talking) return; // idle plays freely while talking
    const clipDur = action.getClip()?.duration || 1;
    // Precedence: live HUD bridge → URL param → prop default.
    const bpmOverride = gaitBridge.bpm ?? (q('bpm') > 0 ? q('bpm') : null) ?? gaitBpm;
    const bpm = bpmOverride || useWorldStore.getState().modulation?.bpm || 92;
    const beatFreq = bpm / 60;
    const MIN = 1.2, MAX = 1.9;                        // comfortable footfalls/sec
    let beatsPerStep = 1;
    for (let i = 0; i < 4 && beatFreq / beatsPerStep > MAX; i++) beatsPerStep *= 2; // fast → step less often
    for (let i = 0; i < 3 && beatFreq / beatsPerStep < MIN; i++) beatsPerStep /= 2; // slow → step more often
    beatsPerStep *= (gaitBridge.gaitoct ?? (q('gaitoct') > 0 ? q('gaitoct') : gaitOctave)); // gait: octave-down
    const beatsPerLoop = beatsPerStep * stepsPerLoop;
    const off = gaitBridge.footphase ?? (q('footphase') != null ? q('footphase') : footPhase);
    const B = state.clock.getElapsedTime() * beatFreq;          // continuous beat count
    const loopPhase = ((B / beatsPerLoop + off) % 1 + 1) % 1;
    action.time = loopPhase * clipDur;                          // paused action still poses at .time
  });

  // Hold the pose, and keep it breathing. A held pose with no motion at all
  // reads as a mannequin within about two seconds — worse than an idle loop,
  // because stillness in a moving room is what the eye goes to. So: a sine on
  // the spine (breath), a slower one on the hips (weight shift), a slow drift
  // on the neck (attention).
  //
  // DESYNCED PER BODY via phaseOffset. Without it a salon of posed people
  // inhales in unison, which is unmistakable and much worse than stillness.
  //
  // Runs in the same post-mixer window as the head-look below. With no clip
  // playing there is nothing to fight, but the ordering still matters: the
  // talking head-look must be free to override the pose's neck.
  useFrame((state) => {
    const list = poseBones.current;
    if (!poseLive || !list.length || !posed) return;
    const t = state.clock.getElapsedTime() + phaseOffset * 10;
    const breath = Math.sin(t * 0.9) * 0.014 * posed.breath;   // ~4s cycle, chest
    const shift = Math.sin(t * 0.33) * 0.02 * posed.sway;      // ~19s cycle, hips
    const drift = Math.sin(t * 0.21 + 1.3) * 0.03 * posed.sway;
    for (const { bone, base, offQ } of list) {
      const n = bone.name.toLowerCase();
      let ax = 0, ay = 0, az = 0;
      if (n.startsWith('spine')) ax = breath;
      else if (n === 'hips') { az = shift; ay = shift * 0.5; }
      else if (n === 'neck') ay = drift;
      bone.quaternion.copy(base).multiply(offQ);
      if (ax || ay || az) {
        LIFE_E.set(ax, ay, az, 'XYZ');
        bone.quaternion.multiply(LIFE_Q.setFromEuler(LIFE_E));
      }
    }
  });

  // Talking gesture — drive the head bone from the reply's live amplitude
  // (talkBridge). Runs AFTER the mixer, so it overrides the clip's head channel.
  // Only while (fading in/out of) speech, so a walking resident keeps its own
  // head motion. Body-only stand-in until a face rig gives us a jaw/visemes.
  const ampSmooth = useRef(0);
  /* How loud this body's voice is right now, 0..1 — ONE definition, read by the
     head-look below and by the mouth driver further down. A conversation line has
     no live amplitude to follow (it plays through its own element); a speech-like
     rhythm stands in for it. */
  const talkTarget = (t) => (voiceTalking ? Math.min(1, talkBridge.amplitude * 1.7)
    : saying ? 0.28 + 0.26 * Math.abs(Math.sin(t * 5.1 + phaseOffset * 6)) * Math.abs(Math.sin(t * 1.7)) : 0);
  useFrame((state) => {
    const h = headBone.current;
    if (!h) return;
    const b = headBase.current;
    // LIVE performer face capture wins — the webcam drives the head directly.
    if (engaged && faceBridge.active) {
      h.rotation.x = b.x + faceBridge.pitch;
      h.rotation.y = b.y + faceBridge.yaw;
      h.rotation.z = b.z + faceBridge.roll;
      return;
    }
    const t = state.clock.getElapsedTime();
    const targetAmp = talkTarget(t);
    ampSmooth.current += (targetAmp - ampSmooth.current) * 0.18;
    const a = ampSmooth.current;
    if (a < 0.004 && !talking) return;
    h.rotation.x = b.x + a * 0.22 + Math.sin(t * 7.5) * a * 0.05;   // nod into emphasis
    h.rotation.y = b.y + Math.sin(t * 2.1) * (0.03 + a * 0.06);      // gentle turn
    h.rotation.z = b.z + Math.sin(t * 1.3) * a * 0.03;               // slight tilt
  });

  // Proximity → tier + attention (§4.1). When someone comes within the near
  // zone, the NPC TURNS TO FACE them — the "it noticed me" beat, zero inference.
  // Reports far/mid/near transitions so the wrapper can label + trigger ambient.
  const groupRef = useRef();
  const tierRef = useRef('far');
  useFrame((state) => {
    if (attentionRadius == null) return; // static NPCs opt out
    const g = groupRef.current;
    if (!g) return;
    const cam = state.camera;
    const dx = cam.position.x - position[0];
    const dz = cam.position.z - position[2];
    const dist = Math.hypot(dx, dz);
    const near = dist < attentionRadius;
    const targetY = near ? Math.atan2(dx, dz) : rotationY; // face the user, else rest pose
    let d = targetY - g.rotation.y;
    d = Math.atan2(Math.sin(d), Math.cos(d));              // shortest arc
    g.rotation.y += d * 0.08;                              // ease, don't snap
    const tier = near ? 'near' : dist < (midRadius ?? attentionRadius * 2.4) ? 'mid' : 'far';
    if (tier !== tierRef.current) { tierRef.current = tier; onTier?.(tier); }
  });


  /* ── WHAT THE WEBSITE'S MODEL LAB TUNED, HONOURED HERE ────────────────────
     Khari, 2026-08-31: *"turn on the video faces, and make sure they animate
     when the avatar is chatting/talking"* / *"their idle settings (shoulder,
     elbows, etc.) made in the website editor should also reflect here."*

     Both come off ONE stored object — `world_assets.variants[].pose`, served on
     `/api/world/avatar-models` as `pose`. It carries the rig corrections
     (shoulderDrop, armOut, elbowIn, headTilt, armLiftMax) and the face box with
     its mouth loop. They are properties of the BODY, not of any animation,
     which is why every surface that draws it should apply them.

     ⚠ NOTE THE NAME. `pose` is already taken in this component by the FIXTURE
     key from poses.js ('sitting', 'barber', …) — a completely different thing.
     The stored rig data is `rigPose`, and conflating the two would be a very
     quiet disaster: one is a string, the other an object, and `if (pose)` is
     true for both. */
  const rigPoseRef = useRef(rigPose);
  // The owner moved the surface slider: re-applied to this body's own materials.
  const glossNow = resolveGloss(rigPose);
  useEffect(() => {
    if (METAL_OVERRIDE) return;
    model.traverse((o) => {
      if (!o.isSkinnedMesh) return;
      for (const m of (Array.isArray(o.material) ? o.material : [o.material])) setMaterialGloss(m, glossNow);
    });
  }, [model, glossNow]);
  rigPoseRef.current = rigPose;
  const engagedRef = useRef(engaged);
  engagedRef.current = engaged;

  // ── the speaking face ──
  const faceRef = useRef(null);
  /* ⚠ `speaking` IS DELIBERATELY NOT IN THE KEY. It flips on every turn of a
     conversation, and re-cutting the patch each time would be both wasteful and
     visible. Starting and stopping the clip is a separate, cheap call. */
  const faceKey = useMemo(() => facePanelKey(facePanelFor(rigPose, { speaking: false })), [rigPose]);
  /* A body that arrives WITHOUT a face because the room's decode budget was
     already spent, and then becomes the one being spoken to, has to be able to
     claim a slot — it is the only body whose mouth actually moves. Bumped at
     most once per engagement, and never for a body that already has a face. */
  const [faceClaim, setFaceClaim] = useState(0);
  useEffect(() => {
    if (engaged && faceKey && !faceRef.current) setFaceClaim((n) => n + 1);
  }, [engaged, faceKey]);
  useEffect(() => {
    if (!model || !faceKey) return undefined;
    /* ⚠ A BODY WITH ITS OWN MOVING FACE NEVER GETS THE VIDEO PATCH. Upgraded
       bodies carry the ICT face (real eyes, teeth, 53 shapes) and the mouth
       driver below moves it; a video projected on top would be two faces. The
       stored `pose.face.video` is switched off for those bodies too, but a
       stale pose must not be able to bring it back. */
    if (hasFaceShapes(model)) return undefined;
    const panel = facePanelFor(rigPoseRef.current, { speaking: false });
    if (!panel) return undefined;
    const mounted = mountFaceDecal(model, panel, { priority: engagedRef.current, label: who });
    faceRef.current = mounted;
    if (mounted) console.log(`[face] ${who}: ${mounted.info.triangles} triangles`);
    return () => { mounted?.dispose(); faceRef.current = null; };
    // `engaged` is read through a ref on purpose: a change of chat focus must
    // not re-cut a face that is already mounted. `faceClaim` is the one path
    // that deliberately does re-run this.
  }, [model, faceKey, who, faceClaim]);
  /* ⚠ THE MOUTH MOVES ON `talking`, WHICH IS ALREADY THE RIGHT SIGNAL — engaged
     (this is the avatar being chatted to) AND residentTalking (a reply is
     actually playing). Declared after the mount effect so `faceRef` is set. */
  useEffect(() => { faceRef.current?.setSpeaking(talking); }, [talking]);

  /* ── the moving face (ICT bodies) ──
     The same voice level that nods the head opens the jaw, shapes the lips and,
     always, blinks. Null for every body without face shapes, which keep the
     video path above untouched. Per-body seed so a crowd never blinks in step. */
  const mouthRef = useRef(null);
  useEffect(() => {
    const m = model ? createMouthDriver(model, { seed: (phaseOffset || 0) * 97 }) : null;
    mouthRef.current = m;
    if (m) console.log(`[face] ${who}: own face, ${m.meshes.length} shaped meshes — mouth follows the voice`);
    return () => { m?.reset(); mouthRef.current = null; };
  }, [model, who]);
  useFrame((state, dt) => {
    const m = mouthRef.current;
    if (m) m.update(dt, talkTarget(state.clock.getElapsedTime()), talking);
  });

  // ── the rig corrections ──
  /* ⚠ EVERY BODY GETS A CORRECTOR NOW, NOT ONLY THE TUNED ONES (2026-10-28).
     Khari: *"make the 3d models work without the user having to make fixes to
     the skeleton."* The corrector also runs the shoulder band
     (three/shoulderBand.js), which keeps the shoulders level whatever clip is
     playing. A body nobody tuned gets the default `shoulderDrop` the tuned
     bodies agree on; an owner's own number, including 0, always wins. */
  const fixVals = useMemo(() => ({
    shoulderDrop: resolveShoulderDrop(rigPose),
    armOut: Number(rigPose?.armOut) || 0,
    armOutFade: Number(rigPose?.armOutFade) || 0,
    elbowIn: Number(rigPose?.elbowIn) || 0,
    headTilt: Number(rigPose?.headTilt) || 0,
    armLiftMax: Number(rigPose?.armLiftMax) || 0,
    // Per-body escape hatch: `false` turns the band off, `{lo, hi}` moves it.
    band: rigPose?.band ?? null,
  }), [rigPose]);
  /* Keyed on the VALUES, not on the object — a parent that rebuilds its model
     list each render hands us a new `rigPose` every time, and rebuilding the
     corrector per render would throw away the very state that makes it
     idempotent. */
  const fixKey = fixVals ? JSON.stringify(fixVals) : '';
  const fixValsRef = useRef(fixVals);
  fixValsRef.current = fixVals;
  const fixRef = useRef(null);
  useEffect(() => {
    if (!model || !fixKey) { fixRef.current = null; return undefined; }
    const fx = createPoseFix(model);
    if (!fx.usable) {
      /* Only worth saying when somebody asked for corrections. A skeleton with
         other bone names (the hand-built NPCs) simply stands as authored. */
      if (hasPoseFix(rigPoseRef.current)) {
        console.warn(`[pose] ${who}: stored rig corrections cannot be applied — no Left/RightArm on this skeleton.`);
      }
      fixRef.current = null;
      return undefined;
    }
    fixRef.current = fx;
    return () => { fixRef.current = null; };
  }, [model, fixKey, who]);

  /* ⚠ REGISTERED LAST, AND THAT IS THE WHOLE POINT. These corrections are
     ADDITIVE (`rotateOnWorldAxis`), so they must land on top of every other
     writer, not underneath one. In this component there are three: the mixer
     (registered first, inside `useAnimations`), the pose/life loop, and the
     talking head-look. react-three-fiber runs same-priority frame callbacks in
     REGISTRATION ORDER, so "declared after all of them" is the ordering — move
     this block up and the head-look silently overwrites the chin lift.

     The corrector is idempotent regardless (see poseFix.js), which is what
     saves the case this ordering cannot: the head-look STOPS WRITING when
     someone stops talking. */
  useFrame((_, dt) => {
    faceRef.current?.step(dt);
    fixRef.current?.apply(fixValsRef.current, dt);
  });

  // ── THE LAMPS (see utils/residentLamps) ──
  // OUTDOORS: same rig, same numbers, same positions as the <pointLight>s they
  // replace — evaluated in this body's own shaders instead of in every shader in
  // the city. The only thing the real lights did beyond the figure was the pool
  // on the pavement, which the calibration notes above were already fighting.
  //
  // INDOORS THEY STAY REAL LIGHTS. A room is small, its light count is fixed
  // while you are in it, and — checked side by side in the salon — the lamps'
  // spill onto the walls and counters around each body is part of how every
  // room was tuned by eye. Taking it away darkens the room, not just the edges.
  const lampU = useMemo(() => createLampUniforms(), []);
  const lamps = useMemo(() => {
    if (!(LIT > 0)) return [];
    const h = height;
    return indoor
      ? [
          { pos: [0, h * 1.75, h * 0.45], intensity: IN_KEY * lampFalloff * lightScale, distance: 4.5 * hScale, color: '#ffe6c2' },
          { pos: [-h * 0.6, h * 1.2, h * 0.35], intensity: IN_FILL * lampFalloff * lightScale, distance: 4 * hScale, color: '#dfe8ff' },
          // Rim from behind — the one that stops a dark body disappearing into
          // a dark wall, which is the actual complaint at night.
          { pos: [0, h * 1.35, -h * 0.7], intensity: IN_RIM * lampFalloff * lightScale, distance: 3.2 * hScale, color: '#fff2e0' },
        ]
      : [
          // Key ABOVE the head, slightly forward — streetlight geometry, so the
          // hot reflection angle points at the ground, never the camera.
          { pos: [0, h * 1.75, h * 0.45], intensity: KEY * lampFalloff, distance: 3.5 * hScale, color: '#fff7ec' },
          { pos: [-h * 0.6, h * 1.2, h * 0.3], intensity: FILL * lampFalloff, distance: 3 * hScale, color: '#e6ecff' },
        ];
  }, [height, indoor, lightScale, lampFalloff, hScale]);
  // Materials are patched as they appear — the face decal is mounted after the
  // body and re-cut when a face changes, so one pass at load would miss it.
  // A body has a handful of materials; checking twice a second is free.
  const lampScan = useRef(1);
  const shaderLamps = !indoor && lamps.length > 0;
  useFrame((_, dt) => {
    if (!shaderLamps || !groupRef.current) { if (lampU.uLampN.value) lampU.uLampN.value = 0; return; }
    lampScan.current += dt;
    if (lampScan.current > 0.5) {
      lampScan.current = 0;
      model.traverse((o) => {
        if (!o.isMesh) return;
        for (const m of (Array.isArray(o.material) ? o.material : [o.material])) patchLampMaterial(m, lampU);
      });
    }
    groupRef.current.updateWorldMatrix(true, false);
    setLamps(lampU, groupRef.current, lamps);
  });

  const faces = useMemo(() => ({ faces: 'body' }), []);
  const hover = (onSelect || onHover)
    ? {
        onClick: onSelect ? (e) => { e.stopPropagation(); onSelect(); } : undefined,
        onPointerOver: (e) => { e.stopPropagation(); if (onSelect) document.body.style.cursor = 'pointer'; onHover?.(); },
        onPointerOut: () => { if (onSelect) document.body.style.cursor = ''; },
      }
    : {};

  return (
    // Its front is +z (city/facing): ?facing=1 draws it (FacingOverlay). One
    // object per body — userData is written to elsewhere (render/roadMap).
    <group ref={groupRef} position={[position[0], position[1] + yOff - (posed ? posed.drop * height : 0) + seatOff, position[2]]} rotation={[0, rotationY, 0]} userData={faces}>
      <group scale={scale} visible={!seatWaiting} {...hover}><primitive object={model} /></group>
      {indoor && lamps.map((l, i) => (
        <pointLight key={i} position={l.pos} intensity={l.intensity} distance={l.distance} color={l.color} />
      ))}
    </group>
  );
}

export default AnimatedResident;
